import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentArrowDownIcon, 
  ClipboardDocumentIcon,
  ChevronDownIcon,
  CalendarIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

// RAG Threshold Configuration
const RAG_THRESHOLDS = {
  avgSpread: {
    green: 0.25,    // < 0.25%
    amber: 0.5      // 0.25% - 0.5%, > 0.5% = red
  },
  volumeCoverage: {
    green: 100,     // > $100M
    amber: 20       // $20M - $100M, < $20M = red
  },
  uptime: {
    green: 99.5,    // > 99.5%
    amber: 95       // 95% - 99.5%, < 95% = red
  },
  slippage: {
    green: 0.4,     // < 0.4%
    amber: 1.0      // 0.4% - 1.0%, > 1.0% = red
  },
  depthScore: {
    green: 80,      // > 80
    amber: 50       // 50 - 80, < 50 = red
  }
};

// Calculate RAG status for individual metrics
const getMetricRAGStatus = (value, metric) => {
  const thresholds = RAG_THRESHOLDS[metric];
  if (!thresholds) return 'amber';

  switch (metric) {
    case 'avgSpread':
    case 'slippage':
      // Lower is better
      if (value < thresholds.green) return 'green';
      if (value <= thresholds.amber) return 'amber';
      return 'red';
    
    case 'volumeCoverage':
    case 'uptime':
    case 'depthScore':
      // Higher is better
      if (value > thresholds.green) return 'green';
      if (value >= thresholds.amber) return 'amber';
      return 'red';
    
    default:
      return 'amber';
  }
};

// Calculate overall RAG status based on all metrics
const getOverallRAGStatus = (maker) => {
  const statuses = [
    getMetricRAGStatus(maker.avgSpread, 'avgSpread'),
    getMetricRAGStatus(maker.volumeCoverage, 'volumeCoverage'),
    getMetricRAGStatus(maker.uptime, 'uptime'),
    getMetricRAGStatus(maker.slippage, 'slippage'),
    getMetricRAGStatus(maker.depthScore, 'depthScore')
  ];

  // Overall status: red if any red, amber if any amber, green only if all green
  if (statuses.includes('red')) return 'red';
  if (statuses.includes('amber')) return 'amber';
  return 'green';
};

// Mock data for 8 market makers (updated with dollar amounts for Volume Coverage)
const marketMakerData = [
  {
    id: 1,
    name: 'Wintermute',
    avgSpread: 0.12,          // Green (< 0.25%)
    volumeCoverage: 150.5,     // Green (> $100M) - in millions
    uptime: 99.8,             // Green (> 99.5%)
    slippage: 0.3,            // Green (< 0.4%)
    depthScore: 88,           // Green (> 80)
    token: 'ETH',
    exchange: 'Binance',
    spreadTrend: [
      { day: 1, value: 0.15 },
      { day: 2, value: 0.13 },
      { day: 3, value: 0.11 },
      { day: 4, value: 0.12 },
      { day: 5, value: 0.10 },
      { day: 6, value: 0.12 },
      { day: 7, value: 0.12 }
    ],
    volumeTrend: [
      { day: 1, value: 145 },
      { day: 2, value: 152 },
      { day: 3, value: 149 },
      { day: 4, value: 155 },
      { day: 5, value: 147 },
      { day: 6, value: 151 },
      { day: 7, value: 150.5 }
    ]
  },
  {
    id: 2,
    name: 'GSR',
    avgSpread: 0.35,          // Amber (0.25% - 0.5%)
    volumeCoverage: 65.2,     // Amber ($20M - $100M)
    uptime: 98.5,             // Amber (95% - 99.5%)
    slippage: 0.7,            // Amber (0.4% - 1.0%)
    depthScore: 75,           // Amber (50 - 80)
    token: 'BTC',
    exchange: 'Coinbase',
    spreadTrend: [
      { day: 1, value: 0.38 },
      { day: 2, value: 0.33 },
      { day: 3, value: 0.36 },
      { day: 4, value: 0.34 },
      { day: 5, value: 0.32 },
      { day: 6, value: 0.37 },
      { day: 7, value: 0.35 }
    ],
    volumeTrend: [
      { day: 1, value: 62 },
      { day: 2, value: 68 },
      { day: 3, value: 65 },
      { day: 4, value: 71 },
      { day: 5, value: 63 },
      { day: 6, value: 67 },
      { day: 7, value: 65.2 }
    ]
  },
  {
    id: 3,
    name: 'Cumberland',
    avgSpread: 0.08,          // Green (< 0.25%)
    volumeCoverage: 220.1,    // Green (> $100M)
    uptime: 99.9,             // Green (> 99.5%)
    slippage: 0.2,            // Green (< 0.4%)
    depthScore: 94,           // Green (> 80)
    token: 'ETH',
    exchange: 'Kraken',
    spreadTrend: [
      { day: 1, value: 0.09 },
      { day: 2, value: 0.07 },
      { day: 3, value: 0.08 },
      { day: 4, value: 0.06 },
      { day: 5, value: 0.09 },
      { day: 6, value: 0.08 },
      { day: 7, value: 0.08 }
    ],
    volumeTrend: [
      { day: 1, value: 215 },
      { day: 2, value: 224 },
      { day: 3, value: 218 },
      { day: 4, value: 226 },
      { day: 5, value: 216 },
      { day: 6, value: 223 },
      { day: 7, value: 220.1 }
    ]
  },
  {
    id: 4,
    name: 'Flow Traders',
    avgSpread: 0.65,          // Red (> 0.5%)
    volumeCoverage: 15.8,     // Red (< $20M)
    uptime: 92.2,             // Red (< 95%)
    slippage: 1.8,            // Red (> 1.0%)
    depthScore: 45,           // Red (< 50)
    token: 'SOL',
    exchange: 'Bybit',
    spreadTrend: [
      { day: 1, value: 0.68 },
      { day: 2, value: 0.62 },
      { day: 3, value: 0.67 },
      { day: 4, value: 0.64 },
      { day: 5, value: 0.61 },
      { day: 6, value: 0.66 },
      { day: 7, value: 0.65 }
    ],
    volumeTrend: [
      { day: 1, value: 14.2 },
      { day: 2, value: 16.8 },
      { day: 3, value: 15.1 },
      { day: 4, value: 17.2 },
      { day: 5, value: 14.8 },
      { day: 6, value: 16.5 },
      { day: 7, value: 15.8 }
    ]
  },
  {
    id: 5,
    name: 'Amber Group',
    avgSpread: 0.28,          // Amber (0.25% - 0.5%)
    volumeCoverage: 85.4,     // Amber ($20M - $100M)
    uptime: 97.1,             // Amber (95% - 99.5%)
    slippage: 0.6,            // Amber (0.4% - 1.0%)
    depthScore: 72,           // Amber (50 - 80)
    token: 'AVAX',
    exchange: 'OKX',
    spreadTrend: [
      { day: 1, value: 0.31 },
      { day: 2, value: 0.26 },
      { day: 3, value: 0.29 },
      { day: 4, value: 0.27 },
      { day: 5, value: 0.25 },
      { day: 6, value: 0.30 },
      { day: 7, value: 0.28 }
    ],
    volumeTrend: [
      { day: 1, value: 82 },
      { day: 2, value: 88 },
      { day: 3, value: 84 },
      { day: 4, value: 89 },
      { day: 5, value: 83 },
      { day: 6, value: 87 },
      { day: 7, value: 85.4 }
    ]
  },
  {
    id: 6,
    name: 'Jump',
    avgSpread: 0.15,          // Green (< 0.25%)
    volumeCoverage: 185.7,    // Green (> $100M)
    uptime: 99.7,             // Green (> 99.5%)
    slippage: 0.35,           // Green (< 0.4%)
    depthScore: 91,           // Green (> 80)
    token: 'BTC',
    exchange: 'Binance',
    spreadTrend: [
      { day: 1, value: 0.17 },
      { day: 2, value: 0.14 },
      { day: 3, value: 0.16 },
      { day: 4, value: 0.13 },
      { day: 5, value: 0.18 },
      { day: 6, value: 0.15 },
      { day: 7, value: 0.15 }
    ],
    volumeTrend: [
      { day: 1, value: 180 },
      { day: 2, value: 188 },
      { day: 3, value: 183 },
      { day: 4, value: 190 },
      { day: 5, value: 182 },
      { day: 6, value: 187 },
      { day: 7, value: 185.7 }
    ]
  },
  {
    id: 7,
    name: 'Galaxy',
    avgSpread: 0.42,          // Amber (0.25% - 0.5%)
    volumeCoverage: 58.3,     // Amber ($20M - $100M)
    uptime: 96.8,             // Amber (95% - 99.5%)
    slippage: 0.8,            // Amber (0.4% - 1.0%)
    depthScore: 68,           // Amber (50 - 80)
    token: 'MATIC',
    exchange: 'Huobi',
    spreadTrend: [
      { day: 1, value: 0.45 },
      { day: 2, value: 0.39 },
      { day: 3, value: 0.43 },
      { day: 4, value: 0.40 },
      { day: 5, value: 0.38 },
      { day: 6, value: 0.44 },
      { day: 7, value: 0.42 }
    ],
    volumeTrend: [
      { day: 1, value: 55 },
      { day: 2, value: 61 },
      { day: 3, value: 57 },
      { day: 4, value: 62 },
      { day: 5, value: 56 },
      { day: 6, value: 60 },
      { day: 7, value: 58.3 }
    ]
  },
  {
    id: 8,
    name: 'Jane Street',
    avgSpread: 0.75,          // Red (> 0.5%)
    volumeCoverage: 12.9,     // Red (< $20M)
    uptime: 89.5,             // Red (< 95%)
    slippage: 2.1,            // Red (> 1.0%)
    depthScore: 42,           // Red (< 50)
    token: 'LINK',
    exchange: 'Gemini',
    spreadTrend: [
      { day: 1, value: 0.78 },
      { day: 2, value: 0.72 },
      { day: 3, value: 0.76 },
      { day: 4, value: 0.73 },
      { day: 5, value: 0.71 },
      { day: 6, value: 0.77 },
      { day: 7, value: 0.75 }
    ],
    volumeTrend: [
      { day: 1, value: 11.5 },
      { day: 2, value: 14.2 },
      { day: 3, value: 12.8 },
      { day: 4, value: 14.8 },
      { day: 5, value: 12.1 },
      { day: 6, value: 13.9 },
      { day: 7, value: 12.9 }
    ]
  }
];

// Add calculated RAG status to each market maker
const enrichedMarketMakerData = marketMakerData.map(maker => ({
  ...maker,
  ragStatus: getOverallRAGStatus(maker)
}));

// RAG Status Component with Arrow (Updated with new Tailwind colors)
const RAGIndicator = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'green':
        return {
          bg: 'bg-green-500/20',
          border: 'border-green-500/50',
          text: 'text-green-500',
          arrow: '↑',
          label: 'Good'
        };
      case 'amber':
        return {
          bg: 'bg-yellow-500/20',
          border: 'border-yellow-500/50',
          text: 'text-yellow-500',
          arrow: '→',
          label: 'Caution'
        };
      case 'red':
        return {
          bg: 'bg-red-500/20',
          border: 'border-red-500/50',
          text: 'text-red-500',
          arrow: '↓',
          label: 'Risk'
        };
      default:
        return {
          bg: 'bg-cream-100/20',
          border: 'border-cream-100/50',
          text: 'text-cream-100',
          arrow: '→',
          label: 'N/A'
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.border} ${config.text}`}>
      <span className={`text-lg mr-2 ${config.text}`}>{config.arrow}</span>
      {config.label}
    </span>
  );
};

// Sparkline Chart Component
const SparklineChart = ({ data, color = "#4A90E2" }) => {
  return (
    <div className="w-24 h-8">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={color} 
            strokeWidth={2} 
            dot={false}
            activeDot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Market Maker Row Component
const MarketMakerRow = ({ maker, index }) => {
  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="border-b border-white/10 hover:bg-white/5 transition-all duration-200"
    >
      <td className="px-6 py-4 text-cream-100 font-medium text-sm">
        {maker.name}
      </td>
      <td className="px-6 py-4 text-center">
        <div className="flex flex-col items-center space-y-1">
          <span className={`text-sm font-mono ${getMetricRAGStatus(maker.avgSpread, 'avgSpread') === 'green' ? 'text-green-500' : getMetricRAGStatus(maker.avgSpread, 'avgSpread') === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
            {maker.avgSpread}%
          </span>
          <SparklineChart data={maker.spreadTrend} color="#10B981" />
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <div className="flex flex-col items-center space-y-1">
          <span className={`text-sm font-mono ${getMetricRAGStatus(maker.volumeCoverage, 'volumeCoverage') === 'green' ? 'text-green-500' : getMetricRAGStatus(maker.volumeCoverage, 'volumeCoverage') === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
            ${maker.volumeCoverage}M
          </span>
          <SparklineChart data={maker.volumeTrend} color="#4A90E2" />
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <span className={`text-sm font-mono ${getMetricRAGStatus(maker.uptime, 'uptime') === 'green' ? 'text-green-500' : getMetricRAGStatus(maker.uptime, 'uptime') === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
          {maker.uptime}%
        </span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className={`text-sm font-mono ${getMetricRAGStatus(maker.slippage, 'slippage') === 'green' ? 'text-green-500' : getMetricRAGStatus(maker.slippage, 'slippage') === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
          {maker.slippage}%
        </span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className={`text-sm font-mono ${getMetricRAGStatus(maker.depthScore, 'depthScore') === 'green' ? 'text-green-500' : getMetricRAGStatus(maker.depthScore, 'depthScore') === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
          {maker.depthScore}/100
        </span>
      </td>
      <td className="px-6 py-4 text-center">
        <RAGIndicator status={maker.ragStatus} />
      </td>
    </motion.tr>
  );
};

// Helper function to get status arrow symbol
const getStatusArrow = (ragStatus) => {
  switch (ragStatus) {
    case 'green': return '↑';
    case 'amber': return '→';
    case 'red': return '↓';
    default: return '→';
  }
};

// Export functionality
const exportToCSV = (data, fileName) => {
  const headers = ['Market Maker', 'Spread (%)', 'Volume (30d $M)', 'Uptime (%)', 'Slippage (%)', 'Depth Coverage Score', 'Status Arrows'];
  const csvContent = [
    headers.join(','),
    ...data.map(row => [
      `"${row.name}"`,
      `"${row.avgSpread.toFixed(2)}"`,
      `"${row.volumeCoverage.toFixed(1)}"`,
      `"${row.uptime.toFixed(1)}"`,
      `"${row.slippage.toFixed(1)}"`,
      `"${row.depthScore}"`,
      `"${getStatusArrow(row.ragStatus)}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileName}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToPDF = async (data, fileName) => {
  const printWindow = window.open('', '', 'height=900,width=1200');
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Valeon Market Maker Performance Overview</title>
      <style>
        @page {
          size: landscape;
          margin: 0.5in;
        }
        
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
          margin: 0;
          padding: 20px;
          color: #333;
          line-height: 1.4;
        }
        
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 3px solid #0B0E1C;
          padding-bottom: 20px;
        }
        
        .main-title {
          font-size: 28px;
          font-weight: bold;
          color: #0B0E1C;
          margin: 0 0 8px 0;
          letter-spacing: 0.5px;
        }
        
        .subtitle {
          font-size: 16px;
          color: #666;
          margin: 0;
          font-style: italic;
        }
        
        .export-date {
          font-size: 14px;
          color: #888;
          margin-top: 8px;
        }
        
        .table-container {
          width: 100%;
          overflow: hidden;
        }
        
        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin-top: 10px;
          font-size: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        th { 
          background: linear-gradient(135deg, #0B0E1C 0%, #1a1d3a 100%);
          color: white;
          padding: 12px 8px;
          text-align: center;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 1px solid #333;
        }
        
        td { 
          padding: 10px 8px;
          text-align: center;
          border: 1px solid #ddd;
          background-color: #fafafa;
        }
        
        tr:nth-child(even) td {
          background-color: #f5f5f5;
        }
        
        tr:hover td {
          background-color: #e8f4f8;
        }
        
        .market-maker {
          font-weight: 600;
          color: #0B0E1C;
          text-align: left !important;
        }
        
        .status-green { 
          color: #10B981; 
          font-weight: bold;
          font-size: 16px;
        }
        
        .status-amber { 
          color: #F59E0B; 
          font-weight: bold;
          font-size: 16px;
        }
        
        .status-red { 
          color: #EF4444; 
          font-weight: bold;
          font-size: 16px;
        }
        
        .numeric {
          font-family: 'Courier New', monospace;
          font-weight: 500;
        }
        
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 10px;
          color: #999;
          border-top: 1px solid #ddd;
          padding-top: 15px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 class="main-title">Valeon Market Maker Performance Overview</h1>
        <p class="subtitle">Comprehensive Analysis & Performance Metrics</p>
        <div class="export-date">Generated on: ${currentDate}</div>
      </div>
      
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th style="width: 18%;">Market Maker</th>
              <th style="width: 12%;">Spread (%)</th>
              <th style="width: 15%;">Volume (30d, $M)</th>
              <th style="width: 12%;">Uptime (%)</th>
              <th style="width: 13%;">Slippage (%)</th>
              <th style="width: 18%;">Depth Coverage Score</th>
              <th style="width: 12%;">Status Arrows</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td class="market-maker">${row.name}</td>
                <td class="numeric">${row.avgSpread.toFixed(2)}%</td>
                <td class="numeric">$${row.volumeCoverage.toFixed(1)}M</td>
                <td class="numeric">${row.uptime.toFixed(1)}%</td>
                <td class="numeric">${row.slippage.toFixed(1)}%</td>
                <td class="numeric">${row.depthScore}/100</td>
                <td class="status-${row.ragStatus}">${getStatusArrow(row.ragStatus)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      
      <div class="footer">
        <p>Valeon Strategic Advisory | Market Maker Performance Analysis<br>
        This report contains ${data.length} market makers analyzed across multiple performance metrics.</p>
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.focus();
  
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
};

const MarketMakerPerformance = () => {
  const [selectedToken, setSelectedToken] = useState('All');
  const [selectedExchange, setSelectedExchange] = useState('All');
  const [selectedDateRange, setSelectedDateRange] = useState('30d');

  // Filter data based on selected filters
  const filteredData = useMemo(() => {
    return enrichedMarketMakerData.filter(maker => {
      const tokenMatch = selectedToken === 'All' || maker.token === selectedToken;
      const exchangeMatch = selectedExchange === 'All' || maker.exchange === selectedExchange;
      return tokenMatch && exchangeMatch;
    });
  }, [selectedToken, selectedExchange]);

  // Get unique tokens and exchanges for filter options
  const tokens = ['All', ...new Set(enrichedMarketMakerData.map(maker => maker.token))];
  const exchanges = ['All', ...new Set(enrichedMarketMakerData.map(maker => maker.exchange))];

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass-card p-8"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-2">
              Market Maker Performance
            </h2>
            <p className="text-cream-200">
              Real-time monitoring and analysis of market maker performance across exchanges and tokens.
            </p>
          </div>
          
          {/* Export Buttons */}
          <div className="flex space-x-3 mt-4 lg:mt-0">
            <button
              onClick={() => exportToCSV(filteredData, 'Market_Maker_Performance_Export')}
              className="flex items-center space-x-2 px-4 py-2 bg-accent-blue/20 border border-accent-blue/30 text-accent-blue rounded-lg hover:bg-accent-blue/30 transition-all duration-200"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              <span className="text-sm font-medium">CSV</span>
            </button>
            <button
              onClick={() => exportToPDF(filteredData, 'Market_Maker_Performance_Export')}
              className="flex items-center space-x-2 px-4 py-2 bg-accent-gold/20 border border-accent-gold/30 text-accent-gold rounded-lg hover:bg-accent-gold/30 transition-all duration-200"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              <span className="text-sm font-medium">PDF</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap gap-4 mb-8 p-4 bg-white/5 rounded-xl border border-white/10"
        >
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-cream-300" />
            <span className="text-cream-200 font-medium text-sm">Filters:</span>
          </div>
          
          {/* Token Filter */}
          <div className="relative">
            <select
              value={selectedToken}
              onChange={(e) => setSelectedToken(e.target.value)}
              className="appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2 pr-8 text-cream-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50"
            >
              {tokens.map(token => (
                <option key={token} value={token} className="bg-primary-800 text-cream-100">
                  {token} {token !== 'All' && 'Token'}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream-300 pointer-events-none" />
          </div>

          {/* Exchange Filter */}
          <div className="relative">
            <select
              value={selectedExchange}
              onChange={(e) => setSelectedExchange(e.target.value)}
              className="appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2 pr-8 text-cream-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50"
            >
              {exchanges.map(exchange => (
                <option key={exchange} value={exchange} className="bg-primary-800 text-cream-100">
                  {exchange} {exchange !== 'All' && 'Exchange'}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream-300 pointer-events-none" />
          </div>

          {/* Date Range Filter */}
          <div className="relative">
            <select
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className="appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2 pr-8 text-cream-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50"
            >
              <option value="30d" className="bg-primary-800 text-cream-100">30 Days</option>
              <option value="90d" className="bg-primary-800 text-cream-100">90 Days</option>
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream-300 pointer-events-none" />
          </div>

          <div className="ml-auto flex items-center space-x-2 text-cream-300 text-sm">
            <CalendarIcon className="h-4 w-4" />
            <span>Showing {filteredData.length} market makers</span>
          </div>
        </motion.div>

        {/* Performance Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="overflow-x-auto"
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/20">
                <th className="px-6 py-4 text-left text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Market Maker
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Avg Spread (30d)
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Volume Coverage (30d)
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Uptime %
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Slippage %
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Depth Coverage
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  RAG Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((maker, index) => (
                <MarketMakerRow key={maker.id} maker={maker} index={index} />
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Summary Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 border-t border-white/10 pt-8"
        >
          <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-6 text-center">
            Performance Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-accent-success/10 border border-accent-success/20 rounded-xl p-6 hover:bg-accent-success/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 bg-accent-success rounded-full shadow-lg"></div>
                <span className="text-accent-success font-semibold text-sm uppercase tracking-wide">Good</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {filteredData.filter(m => m.ragStatus === 'green').length}
              </div>
              <div className="text-cream-300 text-sm">market makers performing well</div>
              <div className="mt-2 text-xs text-accent-success">
                {Math.round((filteredData.filter(m => m.ragStatus === 'green').length / filteredData.length) * 100)}% of filtered results
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-accent-warning/10 border border-accent-warning/20 rounded-xl p-6 hover:bg-accent-warning/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 bg-accent-warning rounded-full shadow-lg"></div>
                <span className="text-accent-warning font-semibold text-sm uppercase tracking-wide">Caution</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {filteredData.filter(m => m.ragStatus === 'amber').length}
              </div>
              <div className="text-cream-300 text-sm">market makers need attention</div>
              <div className="mt-2 text-xs text-accent-warning">
                {Math.round((filteredData.filter(m => m.ragStatus === 'amber').length / filteredData.length) * 100)}% of filtered results
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-accent-danger/10 border border-accent-danger/20 rounded-xl p-6 hover:bg-accent-danger/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 bg-accent-danger rounded-full shadow-lg"></div>
                <span className="text-accent-danger font-semibold text-sm uppercase tracking-wide">Risk</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {filteredData.filter(m => m.ragStatus === 'red').length}
              </div>
              <div className="text-cream-300 text-sm">market makers at risk</div>
              <div className="mt-2 text-xs text-accent-danger">
                {Math.round((filteredData.filter(m => m.ragStatus === 'red').length / filteredData.length) * 100)}% of filtered results
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default MarketMakerPerformance;